import type { OdometerChangeReasonView } from "@credit-acceptance/step6-bff-client";
import { queryOptions, useQuery } from "@tanstack/react-query";

import { workbenchOdometerApi } from "../../../../../api/client";
import { odometerChangeReasonsKeys } from "./odometerChangeReasonsKeys";

// Deal-independent reference data that only turns over when a reason's
// start/end date lapses, so it gets a much longer staleTime than the app-wide
// 30s used for per-deal reads — reopening the Odometer Disclosure modal on a
// second deal shouldn't refetch the same list.
const REASONS_STALE_TIME = 30 * 60_000;

/**
 * Shared queryOptions for the odometer-change reason list (key + queryFn),
 * backed by the generated `WorkbenchOdometerApi.list`. The BFF already narrows
 * the list to the reasons in effect today, so the rows are rendered in server
 * order with no client-side date filtering or sorting.
 *
 * Reusable by useQuery, prefetchQuery, ensureQueryData, getQueryData, and
 * setQueryData.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function odometerChangeReasonsQuery() {
  return queryOptions({
    queryKey: odometerChangeReasonsKeys.list(),
    queryFn: async ({ signal }): Promise<OdometerChangeReasonView[]> => {
      const response = await workbenchOdometerApi.list({ signal });
      return response.data;
    },
    staleTime: REASONS_STALE_TIME,
  });
}

/**
 * Reads the odometer-change reasons offered by the Odometer Disclosure modal.
 * Fetch timing is controlled by mount, not by an `enabled` flag: the only
 * caller lives inside the modal's portal, so the request fires the first time
 * the modal opens and never for a Vehicle section that is merely rendered.
 * @returns {ReturnType<typeof useQuery<OdometerChangeReasonView[]>>} The TanStack Query result.
 */
export function useOdometerChangeReasons() {
  return useQuery(odometerChangeReasonsQuery());
}
